import { ReactNode } from "react";
import { Navigate, useParams } from "react-router-dom";
import { Loader } from "@themes/pollinator/ui/Loader";
import { useSystemVariable } from "@platform/hooks/useSystemVariable";
import {
  INVITATION_TYPE,
  useInvitationByShortcode,
} from "@core/features/invitation";

export type Props = {
  children: ReactNode;
};

/**
 * Closes the public invitation routes while the marketing site is in closed
 * mode — except for organization admin invitations.
 *
 * Admin onboarding runs on its own timeline and routinely continues after
 * participant recruitment for a program has ended. Gating both on the same
 * site-wide switch silently redirects admins to the homepage with no
 * explanation, which is indistinguishable from a broken link.
 */
export function InvitationClosedGate({ children }: Props) {
  const { data, isLoading } = useSystemVariable("marketing_site_mode");
  const { shortcode } = useParams();

  const isClosed = data?.value === "closed";

  const { data: invitation, isLoading: isInvitationLoading } =
    useInvitationByShortcode(isClosed ? shortcode : undefined);

  if (isLoading) {
    return <Loader isFullPage />;
  }

  if (!isClosed) {
    return <>{children}</>;
  }

  if (shortcode && isInvitationLoading) {
    return <Loader isFullPage />;
  }

  const isOrgAdminInvitation =
    invitation?.type === INVITATION_TYPE.ORG_ADMIN ||
    invitation?.type === INVITATION_TYPE.ORG_ADMIN_ONBOARDER;

  if (isOrgAdminInvitation) {
    return <>{children}</>;
  }

  return <Navigate to="/" replace />;
}

export default InvitationClosedGate;
